import axios, { AxiosError, AxiosResponse } from "axios";
import { EditTaskCompletionDataModel, TaskModel, TaskToDeleteModel } from "@teamprevent/common/task/taskActions/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { TaskType, parseTaskModel, CreateTaskModel, CreateImmunisationTaskDto } from "../../task/model";

import { mapCreateTaskDtoToServer, mapCreateTaskModelToServer } from "./ohCaseApiMappers";
import { ActionTaskModel } from "./model";

class OhCaseTaskApi {
    private readonly apiUrl = "/api/case";

    public async assignableTasks(customerId: string, caseId: string): Promise<TaskType[]> {
        const response = await axios.get(`${this.apiUrl}/${customerId}/${caseId}/assignabletasks`);
        return response.data;
    }

    public async createTask(customerId: string, caseId: string, task: CreateTaskModel): Promise<TaskModel> {
        const response = await axios.post<
            ServerModelWithDateStrings<TaskModel>,
            AxiosResponse<ServerModelWithDateStrings<TaskModel>>,
            ServerModelWithDateStrings<CreateTaskModel>
        >(`${this.apiUrl}/${customerId}/${caseId}/tasks`, mapCreateTaskModelToServer(task));
        return parseTaskModel(response.data);
    }

    public async createTasks(customerId: string, caseId: string, tasks: CreateTaskModel[]): Promise<TaskModel[]> {
        const response = await axios.post<
            ServerModelWithDateStrings<TaskModel[]>,
            AxiosResponse<ServerModelWithDateStrings<TaskModel[]>>,
            ServerModelWithDateStrings<CreateTaskModel[]>
        >(`${this.apiUrl}/${customerId}/${caseId}/createTasks`, tasks.map(t => mapCreateTaskModelToServer(t)));
        return response.data.map(parseTaskModel);
    }

    public async cancelTask(caseId: string, taskId: string, closingNote: string): Promise<TaskModel[]> {
        const response = await axios.post(`${this.apiUrl}/${caseId}/${taskId}/cancel`, { closingNote });
        return response.data.map(parseTaskModel);
    }

    public async actionTask(customerId: string, ohCaseId: string, oldTaskId: string, notes: string, followUpTask: CreateImmunisationTaskDto) {
        const data = { followUpTask: mapCreateTaskDtoToServer(followUpTask), notes };
        const response = await axios.post<
            ServerModelWithDateStrings<TaskModel>[],
            AxiosResponse<ServerModelWithDateStrings<TaskModel>[]>,
            ServerModelWithDateStrings<ActionTaskModel>
        >(`${this.apiUrl}/${customerId}/${ohCaseId}/actiontask/${oldTaskId}`, data);
        return response.data.map(parseTaskModel);
    }

    public async editTaskCompletionData(customerId: string, ohCaseId: string, model: EditTaskCompletionDataModel) {
        try {
            const response = await axios.post<TaskModel>(`${this.apiUrl}/${customerId}/${ohCaseId}/editTaskCompletionData`, model);
            return parseTaskModel(response.data);
        } catch (e) {
            const axiosError = e as AxiosError<string>;
            if (axiosError?.response?.status === 400) {
                throw new Error(axiosError.response.data);
            }

            throw e;
        }
    }

    public async deleteTask(customerId: string, ohCaseId: string, model: TaskToDeleteModel) {
        try {
            const response = await axios.post<TaskModel[]>(`${this.apiUrl}/${customerId}/${ohCaseId}/deleteTask`, model);
            return response.data.map(parseTaskModel);
        } catch (e) {
            const axiosError = e as AxiosError<string>;
            if (axiosError?.response?.status === 400) {
                throw new Error(axiosError.response.data);
            }

            throw e;
        }
    }
}

export const ohCaseTaskApi = new OhCaseTaskApi();
